import React, { useState } from 'react';
import { ChatConversation } from '../hooks/useChat';
import { useContactPanel } from '../hooks/useContactPanel';
import { ContactPanelHeader } from './contact-panel/ContactPanelHeader';
import { ContactPanelDeals } from './contact-panel/ContactPanelDeals';
import { ContactPanelTasks } from './contact-panel/ContactPanelTasks';
import { ContactPanelInfo } from './contact-panel/ContactPanelInfo';
import { ContactPanelOrigin } from './contact-panel/ContactPanelOrigin';
import { ContactPanelConversationInfo } from './contact-panel/ContactPanelConversationInfo';
import { ContactPanelObservations } from './contact-panel/ContactPanelObservations';
import ContactFormSheet from '../../../components/shared/ContactFormSheet';
import { cleanPhone, formatInstagramHandle } from '../../../utils/phoneUtils';

interface ContactPanelProps {
    conversation: ChatConversation | undefined;
    connectionId?: string;
    onClose: () => void;
}

const ContactPanel: React.FC<ContactPanelProps> = ({ conversation, connectionId, onClose }) => {
    const {
        openSections,
        toggleSection,
        isCreatingTask,
        setIsCreatingTask,
        currentLeads,
        currentTasks,
        currentContact,
        funnelStages,
        crmUsers,
        isCreatingDeal,
        handleStageChange,
        handleLeadUpdate,
        handleCreateDeal,
        handleCreateTask,
        handleToggleTaskCompletion,
        handleUpdateTask,
        handleDeleteTask,
        conversationResponsible,
        conversationTags,
        handleConversationResponsibleChange,
        handleConversationTagsChange,
        handleDuplicateTask,
        handleUpdateContactField,
        handleSaveContact,
    } = useContactPanel(conversation, connectionId);
    const [isEditingContact, setIsEditingContact] = useState(false);

    if (!conversation) {
        return (
            <div className="flex h-full w-full shrink-0 items-center justify-center bg-card">
                <p className="text-body-sm text-muted">Selecione uma conversa</p>
            </div>
        );
    }

    const instagramHandle = conversation.platform === 'instagram'
        ? (formatInstagramHandle(conversation.metaAttribution?.instagramUsername) ?? null)
        : null;

    return (
        <aside
            id="chat-contact-panel"
            className="relative flex h-full min-w-0 w-full max-w-full flex-col overflow-hidden bg-card"
        >
            <ContactPanelHeader
                conversation={conversation}
                contactName={currentContact?.name}
                instagramHandle={instagramHandle}
                onCollapse={onClose}
                onEditContact={() => setIsEditingContact(true)}
            />

            <div className="chat-panel-scroll scrollbar-premium min-w-0 overflow-x-hidden">
                <ContactPanelInfo
                    isOpen={openSections.contactInfo}
                    onToggle={() => toggleSection('contactInfo')}
                    conversationId={conversation.id}
                    currentContact={currentContact}
                    onUpdateContactField={handleUpdateContactField}
                    chatPlatform={conversation.platform}
                    instagramHandle={instagramHandle}
                />

                <ContactPanelConversationInfo
                    isOpen={openSections.conversationInfo}
                    onToggle={() => toggleSection('conversationInfo')}
                    users={crmUsers}
                    selectedUserId={conversationResponsible}
                    onUserChange={handleConversationResponsibleChange}
                    selectedTagIds={conversationTags}
                    onTagsChange={handleConversationTagsChange}
                />

                <ContactPanelDeals
                    isOpen={openSections.deals}
                    onToggle={() => toggleSection('deals')}
                    currentLeads={currentLeads}
                    onCreateDeal={handleCreateDeal}
                    isCreatingDeal={isCreatingDeal}
                    funnelStages={funnelStages}
                    crmUsers={crmUsers}
                    onStageChange={handleStageChange}
                    onLeadUpdate={handleLeadUpdate}
                />

                <ContactPanelTasks
                    isOpen={openSections.tasks}
                    onToggle={() => toggleSection('tasks')}
                    tasks={currentTasks}
                    isCreatingTask={isCreatingTask}
                    onCreateTask={handleCreateTask}
                    onCancelCreate={() => setIsCreatingTask(false)}
                    onStartCreateTask={() => setIsCreatingTask(true)}
                    onToggleTaskCompletion={handleToggleTaskCompletion}
                    onUpdateTask={handleUpdateTask}
                    onDeleteTask={handleDeleteTask}
                    onDuplicateTask={handleDuplicateTask}
                />

                <ContactPanelOrigin
                    isOpen={openSections.origin}
                    onToggle={() => toggleSection('origin')}
                    currentLead={currentLeads[0]}
                    metaAttribution={conversation?.metaAttribution}
                    remoteJid={conversation.id}
                    currentContact={currentContact}
                    platform={conversation.platform}
                    onUpdateContact={handleUpdateContactField}
                    onLeadUpdate={handleLeadUpdate}
                />

                <ContactPanelObservations
                    isOpen={openSections.observations}
                    onToggle={() => toggleSection('observations')}
                    currentLead={currentLeads[0]}
                    onLeadUpdate={handleLeadUpdate}
                />
            </div>

            <ContactFormSheet
                isOpen={isEditingContact}
                onClose={() => setIsEditingContact(false)}
                onSave={handleSaveContact}
                contained
                lockPhone
                contact={currentContact ?? {
                    id: '',
                    name: conversation.name,
                    whatsapp: cleanPhone(conversation.id),
                    phone: cleanPhone(conversation.id),
                }}
            />
        </aside>
    );
};

export default ContactPanel;
